import { Col, Row } from "antd";
import { startsWith } from "lodash-es";
import { connect } from "react-redux";
import { useState } from "react";
import styled from "styled-components";
import { faCheck, faChevronDown, faTimes } from "@fortawesome/pro-solid-svg-icons";

import { useNoScroll } from "common/hooks";

import {
    UPDATE_USER_MERCHANT_SETTINGS
} from "common/actions/user_merchant_settings";
import {
    SET_NORMALIZING_CURRENCY
} from "common/actions/common";

import { SearchInput } from "common/components/core/textInput";
import { RightPanel } from "common/components/core/rightPanel";

import { Icon } from "common/components/core/icon";
import { getCurrencyName } from "common/helpers/currency";
import { Placeholder } from "common/components/placeholder";
import { Hovercard } from "common/components/hovercard";

import "sass/antd.less";

const mapStateToProps = (state) => ({
    currentPage: state.common.pageName,
    default_currency: state.user_merchant_settings.default_currency,
    currency: state.common.currency,
    currency_seen: state.common.currency_seen,
    dimensions: state.common.merchant_dimensions
});

const mapDispatchToProps = {
    UPDATE_USER_MERCHANT_SETTINGS,
    SET_NORMALIZING_CURRENCY
};

export const CurrencyPicker = connect(mapStateToProps, mapDispatchToProps)((props) => {
    const [expanded, setExpanded] = useState(false);
    const [filter, setFilter] = useState("");
    
    const closePopup = () => setExpanded(false);
    useNoScroll(expanded);
    
    const allKnownCurrencies = [
        ...(props.dimensions.data.currency_alias || []),
        ...props.currency_seen,
        props.currency,
        props.default_currency
    ].filter((c) => c && c !== "GBP-Multi");
    const allCurrencies = Array.from(new Set(allKnownCurrencies)).filter((c) => startsWith(c.toLowerCase(), filter.toLowerCase())).sort();
    
    const changeCurrency = (newValue) => () => {
        props.UPDATE_USER_MERCHANT_SETTINGS({
            default_currency: newValue
        });
        props.SET_NORMALIZING_CURRENCY({
            currency: newValue,
            currency_source: "user-default-manual-override"
        });
        
        closePopup();
    };
    
    // Do not render the picker when only one currency is available
    if (allCurrencies.length === 1 && filter.length === 0) return null;
    
    // Do not render the picker in reconciliation
    if (props.currentPage === "reconciliation") return null;
    
    return (
        <>
            <Hovercard header="CURRENCY NORMALIZER" className="auto-width" body={HoverCardBody} visible={!expanded}>
                <SelectedCurrency className="selected-currency" onClick={() => setExpanded(!expanded)}>
                    {props.currency} <ChevronDownIcon />
                </SelectedCurrency>
            </Hovercard>
            <RightPanel
                open={expanded}
                onClose={closePopup}
                closeOnDocumentClick
                closeOnEscape
                repositionOnResize
            >
                <CurrencyPickerContent>
                    <header>
                        <Icon $shouldHover fontSize={4} className="header-close" icon={faTimes} onClick={closePopup}/>CURRENCY NORMALIZER
                    </header>
                    { props.dimensions.status === "loading" && (
                        <div>
                            <Placeholder size="long" />
                            <br/>
                            <Placeholder size="long" />
                            <Placeholder size="long" />
                            <Placeholder size="long" />
                        </div>
                    )}
                    { props.dimensions.status === "loaded" && (
                        <>
                            <SearchInput placeholder="Search" value={filter} onChange={(e) => setFilter(e.target.value)} />
                            <AllCurrencies>
                                {allCurrencies.map((c) => (
                                    <CurrencyItem key={c} $selected={c === props.currency} onClick={changeCurrency(c)}>
                                        <Col span={2}>{c === props.currency && <Icon css={`color: ${({ theme }) => theme.colors["green-leaf"]}`} fontSize={4} icon={faCheck} />}</Col>
                                        <Col span={3}>{c}</Col>
                                        <Col>{getCurrencyName(c)}</Col>
                                    </CurrencyItem>
                                ))}
                            </AllCurrencies>
                        </>
                    )}
                </CurrencyPickerContent>
            </RightPanel>
        </>
    );
});

const AllCurrencies = styled.div`
    padding-top: ${({ theme }) => theme.space(0.5)};
`;

const CurrencyPickerContent = styled.div`
    padding: ${(props) => props.theme.space(1.5)};
    display: flex;
    flex-direction: column;
    height: 100%;

    header {
        display: flex;
        align-items: center;
        color: ${(props) => props.theme.colors["gray-mid"]};
        margin-bottom: ${(props) => props.theme.space(1.5)};

        ${Icon} {
            margin-right: ${(props) => props.theme.space(1.5)};
        }
    }

    ${AllCurrencies} {
        flex: 1;
        overflow: auto;
    }
`;

const CurrencySource = styled.div`
    padding-top: ${(props) => props.theme.space(1)};
`;

const ChevronDownIcon = styled(Icon).attrs({ icon: faChevronDown })`
    margin-left: 10px;
`;

const SelectedCurrency = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100px;
    height: 50px;
    text-align: center;
    font-size: ${(props) => props.theme.fontSizeLarge};
    background-color: ${(props) => props.theme.colors.white};

    &:hover {
        cursor: pointer;
        background: ${(props) => props.theme.colors["canvas-light"]};

        ${ChevronDownIcon} {
            color: ${(props) => props.theme.colors["gray-dark"]};
        }
    }
`;

const CurrencyItem = styled(Row)`
    font-size: ${(props) => props.theme.fontSizeLarge};
    margin: ${(props) => props.theme.space(0.25)} 0 0 0;
    padding: ${(props) => props.theme.space(0.25)} ${(props) => props.theme.space(1)};
    display: flex;
    align-items: center;

    ${(props) => props.$selected && `
        font-family: ${props.theme.text.medium};
    `}

    &:hover {
        cursor: pointer;
        background: ${(props) => props.theme.colors["canvas-mid"]};
    }
`;

const HoverCardBody = (
    <div>
        <div>Compare amounts &apos;apples-to-apples&apos;<br/>by presenting them as a single currency.</div>
        <CurrencySource>(Calculated using openexchangerates.org)</CurrencySource>
    </div>
);
